import { useState, useRef, useEffect } from 'react'

function cleanName(name) {
  if (!name) return null
  return String(name).replace(/^["']|["']$/g, '')
}

// Rekurzivně vygeneruj všechny cesty od node k listům (bez padding)
function getAllPaths(node, currentPath = []) {
  const nodeValue = node.value ?? cleanName(node.name)
  const newPath = [...currentPath, nodeValue]

  if (!node.children || node.children.length === 0) {
    // Leaf node - vrátit cestu bez padding
    return [newPath]
  }

  const allPaths = []
  node.children.forEach(child => {
    allPaths.push(...getAllPaths(child, newPath))
  })
  return allPaths
}

// Porovnání hodnot s tolerancí na null vs '—'
function valuesEqual(a, b) {
  if (a === b) return true
  if ((a === null && b === '—') || (a === '—' && b === null)) return true
  return false
}

// Kontrola, jestli je cesta vybrána v selected
function isPathSelected(path, selected) {
  return selected.some(selPath =>
    path.every((val, idx) => valuesEqual(val, selPath[idx]))
  )
}

function HierarchyNode({
  node,
  level = 0,
  selected = [],
  onToggle,
  parentPath = [],
}) {
  const [expanded, setExpanded] = useState(false)
  const checkboxRef = useRef(null)
  const rawName = cleanName(node.name)
  const displayName = rawName ?? '—'
  const hasChildren = node.children && node.children.length > 0
  const nodeValue = node.value ?? rawName ?? '—'

  // Kontrola, jestli je vybrána nějaká cesta přes tento node
  const currentPath = [...parentPath, nodeValue]
  const isSelected = selected.some(selPath =>
    currentPath.every((val, idx) => valuesEqual(val, selPath[idx]))
  )

  // Počítej vybrané přímé děti (cesty)
  let selectedCount = 0
  let totalCount = 0

  if (hasChildren) {
    node.children.forEach(child => {
      const childValue = child.value ?? cleanName(child.name)
      const childPath = [...currentPath, childValue]
      totalCount++
      if (selected.some(selPath => childPath.every((v, i) => valuesEqual(v, selPath[i])))) {
        selectedCount++
      }
    })
  }

  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate
    }
  }, [isIndeterminate])

  const handleToggle = () => {
    onToggle(nodeValue, !isSelected, level, parentPath, node)
  }

  return (
    <div>
      <div className="flex items-center gap-1 py-0.5 px-1 hover:bg-gray-50 rounded text-xs">
        {hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 shrink-0">
            {expanded ? '▼' : '▶'}
          </button>
        ) : (
          <div className="w-4" />
        )}

        <input ref={checkboxRef} type="checkbox" checked={isSelected} onChange={handleToggle} className="accent-blue-500 cursor-pointer w-3.5 h-3.5 shrink-0" />

        <span className="flex-1 cursor-pointer hover:font-medium" onClick={handleToggle}>
          {displayName}
        </span>
      </div>

      {expanded && hasChildren && (
        <div className="ml-2 border-l border-gray-100 pl-1">
          {node.children.map((child, idx) => (
            <HierarchyNode
              key={`${level}-${idx}`}
              node={child}
              level={level + 1}
              selected={selected}
              onToggle={onToggle}
              parentPath={[...parentPath, nodeValue]}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function HierarchyFilter({
  hierarchy,
  selected = [],
  onChange,
}) {
  // Normalizuj cestu: '—' → null (pro konzistentní ukládání)
  const normalizePath = (path) => path.map(val => val === '—' ? null : val)

  const handleToggle = (nodeValue, shouldAdd, _, parentPath, node) => {
    let newSelected = selected.map(path => [...path])
    const currentPath = normalizePath([...parentPath, nodeValue])

    if (shouldAdd) {
      const paths = getAllPaths(node, parentPath)
      const normalizedPaths = paths.map(normalizePath)
      newSelected.push(...normalizedPaths)
    } else {
      newSelected = newSelected.filter(selPath =>
        !currentPath.every((val, idx) => valuesEqual(val, selPath[idx]))
      )
    }

    onChange(newSelected)
  }

  // Pokud je jen 1 item na H1, přeskočit jej a jít rovnou na H2
  const nodesToRender = hierarchy.length === 1 ? (hierarchy[0].children || []) : hierarchy
  const startLevel = hierarchy.length === 1 ? 1 : 0
  const parentPathForChildren = hierarchy.length === 1 ? [hierarchy[0].value ?? cleanName(hierarchy[0].name)] : []

  return (
    <div className="flex flex-col gap-1.5 max-h-96 overflow-y-auto text-xs">
      {nodesToRender.map((node, idx) => (
        <HierarchyNode
          key={`node-${idx}`}
          node={node}
          level={startLevel}
          selected={selected}
          onToggle={handleToggle}
          parentPath={parentPathForChildren}
        />
      ))}
    </div>
  )
}
